import React from 'react';
import { Box, Card, IconButton, Menu, Skeleton } from '@mui/material';
import { MoreVert as MoreVertIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';
import {
  ContentTitle,
  ContentDescription,
  ContentSearchSnippet,
  ContentMeta,
  ContentStats,
  ContentThumbnailBanner,
  ContentProcessingBadge,
  type IllustrationFileType,
} from './atoms';

type Content = Parameters<typeof ContentMeta>[0]['content'] &
  Parameters<typeof ContentStats>[0]['content'] &
  Parameters<typeof ContentTitle>[0]['content'] &
  Parameters<typeof ContentDescription>[0]['content'] &
  Parameters<typeof ContentSearchSnippet>[0]['content'] &
  Parameters<typeof ContentProcessingBadge>[0]['content'] & {
    id?: string;
    thumbnailUrl?: string;
    thumbnail_url?: string;
    mimeType?: string;
    mime_type?: string;
    fileName?: string;
    file_name?: string;
  };

export interface ContentTileProps {
  content: Content;
  variant?: 'grid' | 'compact';
  show?: {
    thumbnail?: boolean;
    description?: boolean;
    meta?: boolean;
    stats?: boolean;
    actions?: boolean;
  };
  onClick?: (content: Content) => void;
  loading?: boolean;
  fileType?: IllustrationFileType;
  thumbnailUrl?: string;
  actions?: React.ReactNode[];
  kebabActions?: React.ReactNode[];
  bookmark?: React.ReactNode;
  bannerExtras?: React.ReactNode;
  selected?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Card-shaped content tile. Composes atoms into a vertical layout with banner
 * on top, body in the middle, and stats/actions row at the bottom. Two variants:
 *
 * - `grid` (default): full tile with hover-reveal action row (replaces stats on hover).
 * - `compact`: smaller tile, no description, no hover-reveal (always shows stats).
 *
 * No auth, no router. All behavior is provided by the caller via `onClick`,
 * `actions`, `kebabActions`, `bookmark`.
 */
export const ContentTile: React.FC<ContentTileProps> = ({
  content,
  variant = 'grid',
  show,
  onClick,
  loading,
  fileType,
  thumbnailUrl,
  actions,
  kebabActions,
  bookmark,
  bannerExtras,
  selected,
  sx,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const closeMenu = () => setMenuAnchorEl(null);
  const showThumbnail = show?.thumbnail !== false;
  const showDescription = show?.description !== false && variant === 'grid';
  const showMeta = show?.meta !== false;
  const showStats = show?.stats !== false;
  const showActionsRow = show?.actions !== false && variant === 'grid' && Array.isArray(actions) && actions.length > 0;
  const isCompact = variant === 'compact';

  if (loading) {
    return (
      <Card sx={[{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
        <Skeleton variant="rectangular" height={isCompact ? 90 : 130} />
        <Box sx={{ p: 2, flex: 1 }}>
          <Skeleton variant="text" height={20} />
          {!isCompact && <Skeleton variant="text" height={16} sx={{ mt: 0.5 }} />}
          <Skeleton variant="text" height={16} sx={{ mt: 1.5, width: '60%' }} />
        </Box>
      </Card>
    );
  }

  const contentForBanner = { ...content, thumbnailUrl: thumbnailUrl ?? content.thumbnailUrl };
  const kebabPresent = Array.isArray(kebabActions) && kebabActions.length > 0;

  return (
    <Card
      onClick={onClick ? () => onClick(content) : undefined}
      sx={[
        (theme) => ({
          position: 'relative',
          // Fixed height in grid mode so banner expansion doesn't grow the card
          // and disturb the surrounding grid. Compact tiles flex to content.
          ...(isCompact ? { height: '100%' } : { height: 320 }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
          ...(isCompact
            ? {}
            : {
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                },
              }),
          ...(selected && {
            borderColor: theme.palette.brand.cyan,
            boxShadow: `0 0 0 2px ${theme.palette.brand.cyan}`,
          }),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {showThumbnail && (
        <ContentThumbnailBanner
          content={contentForBanner}
          fileType={fileType}
          height={isCompact ? 90 : 130}
          // Banner expansion calibrated so a full 3-line description plus
          // meta row fits in the shrunk body without flex-shrink squeezing
          // the title (which would clip the title text at the bottom).
          // Body math at hover: 320 card − 148 banner − 36 stats = 136 body.
          // Body content space (− 21 padding) = 115. Content needed at full
          // 3-line desc ≈ 116. Effectively zero overflow.
          hoverHeight={isCompact ? 90 : 148}
          illustrationSize={isCompact ? 'compact' : 'normal'}
          bookmark={bookmark}
          bannerExtras={bannerExtras}
        />
      )}

      {selected && (
        <Box sx={(theme) => ({
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 3,
          color: theme.palette.brand.cyan,
          background: theme.palette.background.paper,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
        })}>
          <CheckCircleIcon sx={{ fontSize: 20 }} />
        </Box>
      )}

      {/* Body:
            title (fixed) → description (content-height, max 3 lines + "...")
            → flex space → meta (pinned to bottom via marginTop: auto)
            → optional processing badge.
          Description is intentionally NOT flex: 1 — that was making the
          Box stretch beyond its 3-line text and producing empty whitespace
          below the ellipsis.
          Title, meta, and processing badge are `flexShrink: 0` so when the
          body is tight on hover only the description shrinks — title text
          never gets clipped by flex-shrink squeezing its line-height box. */}
      <Box sx={{ padding: isCompact ? '10px 12px 8px' : '13px 16px 8px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <ContentTitle content={content} clamp={1} sx={{ mb: '4px', flexShrink: 0 }} />
        <ContentSearchSnippet content={content} sx={{ mb: '4px', flexShrink: 0 }} />
        {showDescription && (
          <ContentDescription
            content={content}
            clamp={3}
            sx={{ mb: '12px' }}
          />
        )}
        {showMeta && (
          <Box sx={{ marginTop: 'auto', paddingTop: '4px', flexShrink: 0 }}>
            <ContentMeta content={content} />
          </Box>
        )}
        <ContentProcessingBadge content={content} sx={{ mt: '6px', alignSelf: 'flex-start', flexShrink: 0 }} />
      </Box>

      {showStats && (
        <Box
          className="tc-stats"
          sx={(theme) => ({
            padding: isCompact ? '6px 12px 10px' : '8px 16px 12px',
            borderTop: `1px solid ${theme.palette.divider}`,
            transition: 'opacity 0.2s ease',
            ...(showActionsRow && { '.MuiCard-root:hover &': { opacity: 0, pointerEvents: 'none' } }),
          })}
        >
          <ContentStats content={content} />
        </Box>
      )}

      {showActionsRow && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '8px 16px 12px',
            display: 'flex',
            gap: '6px',
            background: 'inherit',
            borderTop: `1px solid ${theme.palette.divider}`,
            opacity: 0,
            transform: 'translateY(4px)',
            transition: 'opacity 0.2s ease 0.04s, transform 0.2s ease 0.04s',
            pointerEvents: 'none',
            '.MuiCard-root:hover &': {
              opacity: 1,
              transform: 'translateY(0)',
              pointerEvents: 'auto',
            },
          })}
        >
          {actions!.map((action, i) => (
            <React.Fragment key={i}>{action}</React.Fragment>
          ))}
        </Box>
      )}

      {/* Kebab in banner top-right — always visible (touch + hover devices). */}
      {kebabPresent && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); }}
          sx={(theme) => ({
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            width: 28,
            height: 28,
            background: theme.palette.mode === 'light' ? 'rgba(255,255,255,.92)' : 'rgba(15,30,51,.92)',
            color: theme.palette.text.primary,
            backdropFilter: 'blur(6px)',
            boxShadow: '0 1px 2px rgba(15,23,42,.06)',
            transition: 'transform 0.15s ease, background 0.15s ease',
            '&:hover': {
              background: theme.palette.mode === 'light' ? 'rgba(255,255,255,.98)' : 'rgba(15,30,51,.98)',
              transform: 'scale(1.08)',
            },
          })}
        >
          <MoreVertIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {kebabPresent && (
        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu} onClick={(e) => e.stopPropagation()}>
          {kebabActions!.map((action, i) => (
            <React.Fragment key={i}>{action}</React.Fragment>
          ))}
        </Menu>
      )}
    </Card>
  );
};

export default ContentTile;
